import { useEffect, Component, type ReactNode, lazy, Suspense } from 'react';
import {
  createRootRoute,
  createRoute,
  redirect,
  Outlet,
  useNavigate,
} from '@tanstack/react-router';
import { useAuthStore } from '@/stores/auth.store';

// ============================================================
// Error boundary — catches React crashes with a user-facing UI
// ============================================================
class ErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
          <div className="max-w-md text-center">
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
              Something went wrong
            </h1>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              An unexpected error occurred. Please try refreshing the page.
            </p>
            {this.state.error && (
              <pre className="mt-4 max-h-32 overflow-auto rounded bg-gray-100 p-3 text-left text-xs text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                {this.state.error.message}
              </pre>
            )}
            <button
              onClick={() => window.location.reload()}
              className="mt-4 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              Refresh page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Lazy-loading fallback spinner
function PageSpinner() {
  return (
    <div className="flex h-full items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-blue-500" />
    </div>
  );
}

// Core pages loaded eagerly (login, register, app shell, chat)
import { LoginPage } from '@/pages/login';
import { RegisterPage } from '@/pages/register';
import { AppShell } from '@/pages/app-shell';
import { AreaChatPage } from '@/pages/area-chat';
import { GuestLoginPage } from '@/pages/guest-login';

// Secondary pages lazy-loaded for bundle splitting
const OrgHomePage = lazy(() => import('@/pages/org-home').then(m => ({ default: m.OrgHomePage })));
const OrgMembersPage = lazy(() => import('@/pages/org-members').then(m => ({ default: m.OrgMembersPage })));
const CreateOrgPage = lazy(() => import('@/pages/create-org').then(m => ({ default: m.CreateOrgPage })));
const ThreadListPage = lazy(() => import('@/pages/thread-list').then(m => ({ default: m.ThreadListPage })));
const ThreadViewPage = lazy(() => import('@/pages/thread-view').then(m => ({ default: m.ThreadViewPage })));
const DmListPage = lazy(() => import('@/pages/dm-list').then(m => ({ default: m.DmListPage })));
const SearchPage = lazy(() => import('@/pages/search').then(m => ({ default: m.SearchPage })));
const AdminDashboardPage = lazy(() => import('@/pages/admin-dashboard').then(m => ({ default: m.AdminDashboardPage })));
const InboxPage = lazy(() => import('@/pages/inbox').then(m => ({ default: m.InboxPage })));
const TodosPage = lazy(() => import('@/pages/todos').then(m => ({ default: m.TodosPage })));
const GroupsPage = lazy(() => import('@/pages/groups'));
const BookmarksPage = lazy(() => import('@/pages/bookmarks'));
const ScheduledMessagesPage = lazy(() => import('@/pages/scheduled-messages').then(m => ({ default: m.ScheduledMessagesPage })));
const SecuritySettingsPage = lazy(() => import('@/pages/security-settings').then(m => ({ default: m.SecuritySettingsPage })));
const AreaGalleryPage = lazy(() => import('@/pages/area-gallery').then(m => ({ default: m.AreaGalleryPage })));
const OrgSettingsPage = lazy(() => import('@/pages/org-settings').then(m => ({ default: m.OrgSettingsPage })));
const HelpPage = lazy(() => import('@/pages/help').then(m => ({ default: m.HelpPage })));
const CreateThreadPage = lazy(() => import('@/pages/create-thread').then(m => ({ default: m.CreateThreadPage })));
const CreateAnnouncementPage = lazy(() => import('@/pages/create-announcement').then(m => ({ default: m.CreateAnnouncementPage })));
const DraftsPage = lazy(() => import('@/pages/drafts').then(m => ({ default: m.DraftsPage })));
const PrivacyPage = lazy(() => import('@/pages/privacy').then(m => ({ default: m.PrivacyPage })));
const TermsPage = lazy(() => import('@/pages/terms').then(m => ({ default: m.TermsPage })));

// Wrapper for lazy components
function withSuspense(LazyComponent: React.LazyExoticComponent<React.ComponentType>) {
  return function SuspenseWrapper() {
    return (
      <Suspense fallback={<PageSpinner />}>
        <LazyComponent />
      </Suspense>
    );
  };
}

// Root layout — just renders Outlet (no chrome)
const rootRoute = createRootRoute({
  component: RootLayout,
});

function RootLayout() {
  const hydrate = useAuthStore((s) => s.hydrate);
  const isLoading = useAuthStore((s) => s.isLoading);

  useEffect(() => {
    hydrate();
  }, []);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-blue-500" />
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <Outlet />
    </ErrorBoundary>
  );
}

// ============================================================
// Public routes (login/register)
// ============================================================

// Guests have area-scoped access only — these routes render 403s/empty pages
// for them. Route-level guard bounces them back to their chat instead of a
// broken screen (the sidebar already hides these links; deep links now match).
function guestBeforeLoad() {
  if (useAuthStore.getState().user?.isGuest) {
    throw redirect({ to: '/' });
  }
}

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/register',
  component: RegisterPage,
});

const guestLoginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/guest-login',
  component: GuestLoginPage,
});

const privacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/privacy',
  component: withSuspense(PrivacyPage),
});

const termsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/terms',
  component: withSuspense(TermsPage),
});

// ============================================================
// Authenticated layout
// ============================================================
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  component: AuthGuard,
});

function AuthGuard() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate({ to: '/login' });
    }
  }, [isAuthenticated]);

  if (!isAuthenticated) return null;

  return <AppShell />;
}

// Index (authenticated default)
const indexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  component: () => {
    // AppShell handles redirect to first org
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold">Welcome to GoodChat</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Select an organization from the sidebar
          </p>
        </div>
      </div>
    );
  },
});

// Org routes
const orgRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/org/$orgSlug',
  component: withSuspense(OrgHomePage),
});

const orgMembersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/org/$orgSlug/members',
  component: withSuspense(OrgMembersPage),
});

const createOrgRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/org/new',
  component: withSuspense(CreateOrgPage),
});

// Org settings route
const orgSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/org/$orgSlug/settings',
  component: withSuspense(OrgSettingsPage),
});

// Area chat route (clean URL: /org/$orgSlug/$areaSlug) — eagerly loaded
const areaChatRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/org/$orgSlug/$areaSlug',
  component: AreaChatPage,
});

// Area gallery route
const areaGalleryRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/org/$orgSlug/$areaSlug/gallery',
  component: withSuspense(AreaGalleryPage),
});

// Create thread route (must be before threadListRoute for correct matching)
const createThreadRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/org/$orgSlug/$areaSlug/threads/new',
  component: withSuspense(CreateThreadPage),
});

// Create announcement route
const createAnnouncementRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/org/$orgSlug/$areaSlug/announcement/new',
  component: withSuspense(CreateAnnouncementPage),
});

// Thread routes
const threadListRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/org/$orgSlug/$areaSlug/threads',
  component: withSuspense(ThreadListPage),
});

const threadViewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/org/$orgSlug/$areaSlug/thread/$threadId',
  component: withSuspense(ThreadViewPage),
});

// DM list / new DM route
const dmListRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/org/$orgSlug/messages',
  beforeLoad: guestBeforeLoad,
  component: withSuspense(DmListPage),
});

// Search route
const searchRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/org/$orgSlug/search',
  component: withSuspense(SearchPage),
});

// Admin dashboard route
const adminDashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/org/$orgSlug/admin',
  beforeLoad: guestBeforeLoad,
  component: withSuspense(AdminDashboardPage),
});

// Inbox route
const inboxRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/org/$orgSlug/inbox',
  beforeLoad: guestBeforeLoad,
  component: withSuspense(InboxPage),
});

// Todos route — one of the three surfaces (Chats · Inbox · Todos).
//
// Deliberately NO guestBeforeLoad, unlike the member-only routes around it: a
// guest can legitimately owe an acknowledgement, have an unanswered poll, or
// hold a room task, and GET /todos already scopes the feed per-area for
// them. Guarding this route would hide work a guest actually owes.
const todosRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/org/$orgSlug/todos',
  component: withSuspense(TodosPage),
});

// Groups route
const groupsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/org/$orgSlug/groups',
  beforeLoad: guestBeforeLoad,
  component: withSuspense(GroupsPage),
});

// Bookmarks route
const bookmarksRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/org/$orgSlug/bookmarks',
  beforeLoad: guestBeforeLoad,
  component: withSuspense(BookmarksPage),
});

// Scheduled messages route
const scheduledMessagesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/org/$orgSlug/scheduled-messages',
  beforeLoad: guestBeforeLoad,
  component: withSuspense(ScheduledMessagesPage),
});

// Security settings route
const securityRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/org/$orgSlug/security',
  component: withSuspense(SecuritySettingsPage),
});

// Drafts route
const draftsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/org/$orgSlug/drafts',
  beforeLoad: guestBeforeLoad,
  component: withSuspense(DraftsPage),
});

// Help route
const helpRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/org/$orgSlug/help',
  component: withSuspense(HelpPage),
});

// ============================================================
// Route tree
// ============================================================
export const routeTree = rootRoute.addChildren([
  loginRoute,
  registerRoute,
  guestLoginRoute,
  privacyRoute,
  termsRoute,
  appRoute.addChildren([
    indexRoute,
    createOrgRoute,
    orgRoute,
    orgMembersRoute,
    orgSettingsRoute,
    areaChatRoute,
    areaGalleryRoute,
    createThreadRoute,
    createAnnouncementRoute,
    threadListRoute,
    threadViewRoute,
    dmListRoute,
    searchRoute,
    adminDashboardRoute,
    inboxRoute,
    todosRoute,
    groupsRoute,
    bookmarksRoute,
    scheduledMessagesRoute,
    draftsRoute,
    securityRoute,
    helpRoute,
  ]),
]);
